import { useCallback, useEffect, useMemo, useReducer } from 'react';

import { APIService } from '../../services/APIService';
import { ActionType, State, reducer } from './reducer';

const { REACT_APP_API_ENDPOINT: API_ENDPOINT, REACT_APP_API_KEY: API_KEY } =
  process.env;

const apiService = new APIService(API_ENDPOINT || '', API_KEY || '');

const initialState: State = {
  sidePanel: 'FILTER',
  panelVisible: true,
  filter: {},
  districtListings: [],
  selectedDistrict: null,
  isDistrictListingsLoading: true,
  isNoticeOpen: true
};

interface DistrictFilterActions {
  togglePanelVisible: () => void;
  openFiltersPanel: () => void;
  closeFiltersPanel: () => void;
  applyMapExtentFilter: (
    extent: [number, number, number, number] | null
  ) => void;
  applyFilters: (
    filters: Pick<
      State['filter'],
      | 'districtName'
      | 'awbdMembership'
      | 'region'
      | 'districtType'
      | 'acreage'
      | 'yearCreated'
      | 'bondsOutstanding'
      | 'assessedValue'
      | 'waterConnections'
      | 'sanitaryConnections'
    >
  ) => void;
  selectDistrictById: (districtId: string) => void;
  clearSelectedDistrict: () => void;
  clearError: () => void;
  openNoticeSnackbar: () => void;
  closeNoticeSnackbar: () => void;
  navigateMapToExtent: (extent: [number, number, number, number]) => void;
}

interface DistrictFilterSelectors {
  /**
   * Filter configurations and default values formatted specifically as a `FilterPanel` prop.
   * Will be undefined if configurations are not yet available.
   * Configurations are fetched asynchronously.
   * Default values are intended for use in uncontrolled inputs
   * and will map to the current filter value.
   */
  filterPanelConfigurations?: {
    districtName: {
      defaultValue: State['filter']['districtName'];
    };
    awbdMembership: {
      defaultValue: State['filter']['awbdMembership'];
    };
    region: {
      defaultValue: State['filter']['region'];
    };
    districtType: {
      defaultValue: State['filter']['districtType'];
      options: NonNullable<State['filterConfigurations']>['districtType'];
    };
    legislativeDistrict: {
      defaultValue: State['filter']['legislativeDistrict'];
      options: NonNullable<
        State['filterConfigurations']
      >['legislativeDistrict'];
    };
    acreage: {
      defaultValue: State['filter']['acreage'];
      histogram: NonNullable<State['filterConfigurations']>['acreage'];
    };
    yearCreated: {
      defaultValue: State['filter']['yearCreated'];
      histogram: NonNullable<State['filterConfigurations']>['yearCreated'];
    };
    bondsOutstanding: {
      defaultValue: State['filter']['bondsOutstanding'];
      histogram: NonNullable<State['filterConfigurations']>['bondsOutstanding'];
    };
    assessedValue: {
      defaultValue: State['filter']['assessedValue'];
      histogram: NonNullable<State['filterConfigurations']>['assessedValue'];
    };
    waterConnections: {
      defaultValue: State['filter']['waterConnections'];
      histogram: NonNullable<State['filterConfigurations']>['waterConnections'];
    };
    sanitaryConnections: {
      defaultValue: State['filter']['sanitaryConnections'];
      histogram: NonNullable<
        State['filterConfigurations']
      >['sanitaryConnections'];
    };
  };
  /** IDs of listed districts */
  districtListingsIds: string[];
}

export const useDistrictFilter = (): State &
  DistrictFilterActions &
  DistrictFilterSelectors => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { filterConfigurations, filter, districtListings } = state;

  /** Useful in detecting new district selections */
  const selectedDistrictId = state.selectedDistrict?.districtId;

  // #region Side Effects
  useEffect(() => {
    const controller = new AbortController();
    apiService
      .fetchFilterConfigurations({}, { abortController: controller })
      .then((configurations) =>
        dispatch({
          type: ActionType.LOAD_FILTER_CONFIGURATIONS,
          payload: { configurations }
        })
      )
      .catch((err) => {
        if (err.message === 'canceled') return;
        dispatch({
          type: ActionType.NETWORK_ERROR,
          payload: { message: 'Unable to load filter configurations' }
        });
      });

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    dispatch({
      type: ActionType.DISTRICT_LISTINGS_SEARCH_START
    });
    const controller = new AbortController();

    apiService
      .queryDistricts(filter, { abortController: controller })
      .then((districtListings) => {
        dispatch({
          type: ActionType.LOAD_DISTRICT_LISTINGS,
          payload: { districtListings }
        });
        dispatch({
          type: ActionType.DISTRICT_LISTINGS_SEARCH_COMPLETE
        });
      })
      .catch((err) => {
        if (err.message === 'canceled') return;
        dispatch({
          type: ActionType.NETWORK_ERROR,
          payload: { message: 'Unable to load district listings' }
        });
        dispatch({
          type: ActionType.DISTRICT_LISTINGS_SEARCH_COMPLETE
        });
      });

    return () => {
      controller.abort();
    };
  }, [filter]);

  useEffect(() => {
    if (!selectedDistrictId) return undefined;

    const controller = new AbortController();
    apiService
      .fetchDistrict(selectedDistrictId, {
        abortController: controller
      })
      .then((districtDetails) =>
        dispatch({
          type: ActionType.LOAD_SELECTED_DISTRICT,
          payload: { districtDetails }
        })
      )
      .catch((err) => {
        if (err.message === 'canceled') return;
        dispatch({
          type: ActionType.NETWORK_ERROR,
          payload: { message: 'Unable to load full district details' }
        });
      });

    return () => {
      controller.abort();
    };
  }, [selectedDistrictId]);
  // #endregion

  // #region Actions
  const togglePanelVisible = useCallback<
    DistrictFilterActions['togglePanelVisible']
  >(() => dispatch({ type: ActionType.TOGGLE_PANEL_VISIBLE }), []);

  const openFiltersPanel = useCallback<
    DistrictFilterActions['openFiltersPanel']
  >(() => dispatch({ type: ActionType.OPEN_FITLERS_PANEL }), []);

  const closeFiltersPanel = useCallback<
    DistrictFilterActions['closeFiltersPanel']
  >(() => dispatch({ type: ActionType.CLOSE_FILTERS_PANEL }), []);

  const applyMapExtentFilter = useCallback<
    DistrictFilterActions['applyMapExtentFilter']
  >((extent) => {
    if (!extent) {
      dispatch({ type: ActionType.CLEAR_MAP_EXTENT_FILTER });
      return;
    }
    dispatch({
      type: ActionType.APPLY_MAP_EXTENT_FILTER,
      payload: { extent }
    });
  }, []);

  const applyFilters = useCallback<DistrictFilterActions['applyFilters']>(
    (filters) => {
      dispatch({ type: ActionType.APPLY_FILTERS, payload: filters });
    },
    []
  );

  const selectDistrictById = useCallback<
    DistrictFilterActions['selectDistrictById']
  >((districtId) => {
    dispatch({ type: ActionType.SELECT_DISTRICT, payload: { districtId } });
  }, []);

  const clearSelectedDistrict = useCallback<
    DistrictFilterActions['clearSelectedDistrict']
  >(() => {
    dispatch({ type: ActionType.CLEAR_SELECTED_DISTRICT });
  }, []);

  const clearError = useCallback<DistrictFilterActions['clearError']>(() => {
    dispatch({ type: ActionType.CLEAR_ERROR });
  }, []);

  const openNoticeSnackbar = useCallback<
    DistrictFilterActions['openNoticeSnackbar']
  >(() => {
    dispatch({ type: ActionType.OPEN_NOTICE_SNACKBAR });
  }, []);

  const closeNoticeSnackbar = useCallback<
    DistrictFilterActions['closeNoticeSnackbar']
  >(() => {
    dispatch({ type: ActionType.CLOSE_NOTICE_SNACKBAR });
  }, []);

  const navigateMapToExtent = useCallback<
    DistrictFilterActions['navigateMapToExtent']
  >((extent) => {
    dispatch({ type: ActionType.NAVIGATE_MAP, payload: { extent } });
  }, []);

  const actions = {
    togglePanelVisible,
    openFiltersPanel,
    closeFiltersPanel,
    applyMapExtentFilter,
    applyFilters,
    selectDistrictById,
    clearSelectedDistrict,
    clearError,
    openNoticeSnackbar,
    closeNoticeSnackbar,
    navigateMapToExtent
  };
  // #endregion

  // #region Selectors
  const filterPanelConfigurations: DistrictFilterSelectors['filterPanelConfigurations'] =
    useMemo(
      () =>
        filterConfigurations
          ? {
              districtName: { defaultValue: filter.districtName },
              awbdMembership: { defaultValue: filter.awbdMembership },
              region: { defaultValue: filter.region },
              districtType: {
                defaultValue: filter.districtType,
                options: filterConfigurations.districtType
              },
              legislativeDistrict: {
                defaultValue: filter.legislativeDistrict,
                options: filterConfigurations.legislativeDistrict
              },
              acreage: {
                defaultValue: filter.acreage,
                histogram: filterConfigurations.acreage
              },
              yearCreated: {
                defaultValue: filter.yearCreated,
                histogram: filterConfigurations.yearCreated
              },
              bondsOutstanding: {
                defaultValue: filter.bondsOutstanding,
                histogram: filterConfigurations.bondsOutstanding
              },
              assessedValue: {
                defaultValue: filter.assessedValue,
                histogram: filterConfigurations.assessedValue
              },
              waterConnections: {
                defaultValue: filter.waterConnections,
                histogram: filterConfigurations.waterConnections
              },
              sanitaryConnections: {
                defaultValue: filter.sanitaryConnections,
                histogram: filterConfigurations.sanitaryConnections
              }
            }
          : undefined,
      [filter, filterConfigurations]
    );

  const districtListingsIds: DistrictFilterSelectors['districtListingsIds'] =
    useMemo(
      () => districtListings.map(({ districtId }) => districtId),
      [districtListings]
    );

  const selectors = { filterPanelConfigurations, districtListingsIds };
  // #endregion

  return {
    ...state,
    ...actions,
    ...selectors
  };
};
